import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import PageLayout from '../../components/ui/PageLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ROIOverview from './components/ROIOverview';
import ChannelPerformance from './components/ChannelPerformance';
import CampaignROITable from './components/CampaignROITable';
import RevenueAttribution from './components/RevenueAttribution';
import ExportReports from './components/ExportReports';
import { roiTrackingService } from '../../services/roiTrackingService';

export default function ROITrackingDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [roiSummary, setRoiSummary] = useState(null);
  const [acquisitionCosts, setAcquisitionCosts] = useState([]);
  const [campaignROI, setCampaignROI] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    end: new Date()?.toISOString()?.split('T')?.[0]
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user?.id, dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [summary, costs, roi, revenue] = await Promise.allSettled([
        roiTrackingService?.getROISummary(dateRange?.start, dateRange?.end),
        roiTrackingService?.getAcquisitionCosts(dateRange?.start, dateRange?.end),
        roiTrackingService?.getCampaignROI(user?.id),
        roiTrackingService?.getRevenueAttribution(dateRange?.start, dateRange?.end)
      ]);

      if (summary?.status === 'fulfilled') setRoiSummary(summary?.value);
      if (costs?.status === 'fulfilled') setAcquisitionCosts(costs?.value || []);
      if (roi?.status === 'fulfilled') setCampaignROI(roi?.value || []);
      if (revenue?.status === 'fulfilled') setRevenueData(revenue?.value || []);
    } catch (err) {
      console.error('Error loading ROI tracking data:', err);
      setError(err?.message || 'Failed to load ROI tracking data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'ROI Overview' },
    { id: 'channels', label: 'Channel Performance' },
    { id: 'campaigns', label: 'Campaign ROI' },
    { id: 'attribution', label: 'Revenue Attribution' },
    { id: 'reports', label: 'Export Reports' }
  ];

  if (loading) {
    return (
      <PageLayout>
        <Helmet><title>ROI Tracking Dashboard - LeadGen Pro AI</title></Helmet>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading ROI tracking data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>ROI Tracking Dashboard - LeadGen Pro AI</title>
        <meta name="description" content="Comprehensive financial performance analysis and attribution tracking" />
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">ROI Tracking Dashboard</h1>
        <p className="text-muted-foreground">Comprehensive financial performance and attribution tracking</p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Date Range:</span>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRange?.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e?.target?.value }))}
              className="px-3 py-1.5 border border-border rounded-lg bg-background text-foreground text-sm"
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="date"
              value={dateRange?.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e?.target?.value }))}
              className="px-3 py-1.5 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadData}>Refresh</Button>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-center space-x-3">
          <Icon name="AlertCircle" size={18} className="text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" onClick={loadData}>Retry</Button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && <ROIOverview data={roiSummary} />}
        {activeTab === 'channels' && <ChannelPerformance data={acquisitionCosts} userId={user?.id} />}
        {activeTab === 'campaigns' && <CampaignROITable data={campaignROI} />}
        {activeTab === 'attribution' && <RevenueAttribution data={revenueData} />}
        {activeTab === 'reports' && <ExportReports userId={user?.id} dateRange={dateRange} />}
      </div>
    </PageLayout>
  );
}